import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import useFocusPulse from '../../hooks/useFocusPulse';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { SavingIndicator } from '../../components/ui/SavingIndicator';
import { useAutoSave } from '../../hooks/useAutoSave';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  UsersRound,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Search,
  Plus,
  Loader2,
  ArrowLeft,
  ChevronRight,
  Trash2,
  Link2,
  RefreshCw,
  Building2
} from 'lucide-react';
import { cn } from '../../lib/utils';
import CapabilityMatrix from '../../components/pursuit/CapabilityMatrix';
import PursuitLifecycleStepper from '../../components/pursuit/PursuitLifecycleStepper';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status colors
const getStatusIcon = (status) => {
  switch (status) {
    case 'MET': return <CheckCircle2 className="w-5 h-5 text-emerald-600" />;
    case 'PARTIAL': return <AlertTriangle className="w-5 h-5 text-amber-600" />;
    case 'GAP': return <XCircle className="w-5 h-5 text-red-600" />;
    default: return <AlertTriangle className="w-5 h-5 text-slate-400" />;
  }
};

const getSeverityBadge = (severity) => {
  if (!severity) return null;
  const colors = {
    HIGH: 'bg-red-100 text-red-700',
    MEDIUM: 'bg-amber-100 text-amber-700',
    LOW: 'bg-blue-100 text-blue-700'
  };
  return (
    <Badge className={cn('text-xs', colors[severity] || 'bg-slate-100 text-slate-600')}>
      {severity}
    </Badge>
  );
};

export default function GapAnalysisLive() {
  const { workspaceId, pursuitId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const focusedGapId = searchParams.get('focus');
  const { getAuthHeaders, user, authToken } = useAuth();
  
  // State
  const [pursuit, setPursuit] = useState(null);
  const [gaps, setGaps] = useState([]);
  const [pastPerformances, setPastPerformances] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingGap, setEditingGap] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Phase 2.3 bonus — Task-Area coverage strip pulled from /coverage-map.
  // Surfaces the federal "Applicable #" cross-link between LCATs and Task
  // Areas so reviewers see at a glance which areas have staffing depth.
  const [coverageMap, setCoverageMap] = useState(null);
  
  // Form state
  const [form, setForm] = useState({
    requirement_name: '',
    requirement_description: '',
    requirement_source: '',
    status: 'GAP',
    severity: 'MEDIUM',
    solution_type: '',
    solution_notes: '',
    mapped_past_performance_id: '',
    mapping_confidence: '',
    mapping_notes: '',
    // v3.11.2 — federal-template parity
    capability_statement: '',
    bidder_response: '',
    // v3.12.2 — Cross-Tool Coverage Map roll-up. When set, the gap appears
    // under its named Task Area on the Coverage Map instead of "Unassigned".
    task_area: '',
  });

  // v3.12.3 — Coverage Map deep-link pulse (shared hook).
  useFocusPulse({
    rows: gaps,
    getSelector: (id) => `[data-testid="gap-row-${id}"]`,
  });

  // Auto-save for individual gap updates
  const saveGap = useCallback(async ({ gapId, data }) => {
    const response = await fetch(`${API_URL}/api/pursuits/${pursuitId}/gaps/${gapId}`, {
      method: 'PATCH',
      headers: getAuthHeaders(),
      body: JSON.stringify(data)
    });
    
    if (!response.ok) throw new Error('Failed to save');
    return await response.json();
  }, [pursuitId, getAuthHeaders]);

  const { save, isSaving, lastSaved, error: saveError } = useAutoSave(saveGap, 800);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Fetch pursuit
        const pursuitRes = await fetch(`${API_URL}/api/pursuits/${pursuitId}`, {
          headers: getAuthHeaders()
        });
        if (pursuitRes.ok) {
          const pursuitData = await pursuitRes.json();
          setPursuit(pursuitData);
        }
        
        // Fetch gaps
        const gapsRes = await fetch(`${API_URL}/api/pursuits/${pursuitId}/gaps`, {
          headers: getAuthHeaders()
        });
        if (gapsRes.ok) {
          const gapsData = await gapsRes.json();
          setGaps(gapsData);
        }
        
        // Fetch past performances for mapping
        const ppRes = await fetch(`${API_URL}/api/kb/past-performances`, {
          headers: getAuthHeaders()
        });
        if (ppRes.ok) {
          const ppData = await ppRes.json();
          setPastPerformances(ppData);
        }

        // Phase 2.3 bonus — non-blocking coverage map fetch
        try {
          const cmRes = await fetch(`${API_URL}/api/pursuits/${pursuitId}/coverage-map`, {
            headers: getAuthHeaders()
          });
          if (cmRes.ok) setCoverageMap(await cmRes.json());
        } catch (e) { /* hint just won't render */ }
      } catch (err) {
        toast.error('Failed to load data');
      } finally {
        setIsLoading(false);
      }
    };

    if (pursuitId) fetchData();
  }, [pursuitId, getAuthHeaders]);

  // Handle form submit (create/update)
  const handleSubmit = async () => {
    if (!form.requirement_name.trim()) {
      toast.error('Requirement name is required');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const payload = {
        ...form,
        mapped_past_performance_id: form.mapped_past_performance_id || null,
        mapping_confidence: form.mapping_confidence ? parseInt(form.mapping_confidence) : null
      };
      
      if (editingGap) {
        // Update
        const res = await fetch(`${API_URL}/api/pursuits/${pursuitId}/gaps/${editingGap.id}`, {
          method: 'PATCH',
          headers: getAuthHeaders(),
          body: JSON.stringify(payload)
        });
        if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.detail || 'Update failed');
        toast.success('Gap updated');
      } else {
        // Create
        const res = await fetch(`${API_URL}/api/pursuits/${pursuitId}/gaps`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(payload)
        });
        if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.detail || 'Create failed');
        toast.success('Gap added');
      }
      
      // Refresh gaps
      const gapsRes = await fetch(`${API_URL}/api/pursuits/${pursuitId}/gaps`, {
        headers: getAuthHeaders()
      });
      if (gapsRes.ok) {
        setGaps(await gapsRes.json());
      }
      
      closeModal();
    } catch (err) {
      toast.error(err?.message || 'Failed to save gap');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete
  const handleDelete = async (gapId) => {
    if (!window.confirm('Delete this gap analysis item?')) return;
    
    try {
      await fetch(`${API_URL}/api/pursuits/${pursuitId}/gaps/${gapId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      setGaps(gaps.filter(g => g.id !== gapId));
      toast.success('Gap deleted');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  // Handle status change (inline auto-save)
  const handleStatusChange = (gapId, newStatus) => {
    setGaps(prev => prev.map(g => 
      g.id === gapId ? { ...g, status: newStatus } : g
    ));
    save({ gapId, data: { status: newStatus } });
  };

  // Handle PP mapping (inline auto-save)
  const handleMappingChange = (gapId, ppId) => {
    const pp = pastPerformances.find(p => p.id === ppId);
    setGaps(prev => prev.map(g => 
      g.id === gapId ? { 
        ...g, 
        mapped_past_performance_id: ppId,
        mapped_past_performance_title: pp?.contract_title
      } : g
    ));
    save({ gapId, data: { mapped_past_performance_id: ppId || null } });
  };

  // Open modal
  const openModal = (gap = null) => {
    if (gap) {
      setEditingGap(gap);
      setForm({
        requirement_name: gap.requirement_name || '',
        requirement_description: gap.requirement_description || '',
        requirement_source: gap.requirement_source || '',
        status: gap.status || 'GAP',
        severity: gap.severity || 'MEDIUM',
        solution_type: gap.solution_type || '',
        solution_notes: gap.solution_notes || '',
        mapped_past_performance_id: gap.mapped_past_performance_id || '',
        mapping_confidence: gap.mapping_confidence?.toString() || '',
        mapping_notes: gap.mapping_notes || '',
        capability_statement: gap.capability_statement || '',
        bidder_response: gap.bidder_response || '',
        task_area: gap.task_area || '',
      });
    } else {
      setEditingGap(null);
      setForm({
        requirement_name: '',
        requirement_description: '',
        requirement_source: '',
        status: 'GAP',
        severity: 'MEDIUM',
        solution_type: '',
        solution_notes: '',
        mapped_past_performance_id: '',
        mapping_confidence: '',
        mapping_notes: '',
        capability_statement: '',
        bidder_response: '',
        task_area: '',
      });
    }
    setIsAddModalOpen(true);
  };

  const closeModal = () => {
    setIsAddModalOpen(false);
    setEditingGap(null);
  };

  // Stats
  const stats = {
    met: gaps.filter(g => g.status === 'MET').length,
    partial: gaps.filter(g => g.status === 'PARTIAL').length,
    gap: gaps.filter(g => g.status === 'GAP').length
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div data-testid="gap-analysis-live" className="min-h-screen bg-slate-50">
      {/* Header — v3.18.20 centered to max-w-7xl */}
      <div className="bg-white border-b border-slate-200 px-8 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 text-sm text-slate-500 mb-1">
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-6 px-2"
                onClick={() => navigate(`/${workspaceId}/pursuit/${pursuitId}/gonogo`)}
              >
                <ArrowLeft className="w-3 h-3 mr-1" />
                Go/No-Go
              </Button>
              <ChevronRight className="w-4 h-4" />
              <span className="text-slate-700">Gap Analysis</span>
            </div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Gap Analysis & Teaming
            </h1>
            {pursuit && (
              <p className="text-sm text-slate-500 mt-1">
                {pursuit.opportunity_name}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            <SavingIndicator isSaving={isSaving} lastSaved={lastSaved} error={saveError} />
            <Button
              className="bg-teal-700 hover:bg-teal-800 text-white"
              onClick={() => openModal()}
              data-testid="add-gap-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Requirement
            </Button>
          </div>
        </div>
      </div>

      <div className="px-8">
        <div className="max-w-7xl mx-auto">
          <PursuitLifecycleStepper />
        </div>
      </div>

      <div className="p-8">
        <div className="max-w-7xl mx-auto">
        {/* v3.10.13: removed 3 redundant Met/Partial/Gaps cards — same
            counts surface in the Capability Matrix & LCAT Map section
            below, plus the Gap Tracker rows themselves carry status
            badges. Less noise, more signal. */}

        {/* Capability Matrix & LCAT Map */}
        <div className="mb-6" data-testid="capability-matrix-section">
          <CapabilityMatrix pursuitId={pursuitId} token={authToken} />
        </div>

        {/* Requirements List */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="border-b border-slate-100">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
              Gap Tracker (PP-Mapped)
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">
              Manually-curated gap list with per-row "Map to Past Performance"
              for differentiation. Distinct from Opportunity Intake (extraction)
              and the Compliance Matrix (proposal mapping) — this is the
              "we've-proven-this-before" tracker.
            </p>

            {/* Phase 2.3 bonus — Federal Task-Area cross-link to Capability Matrix LCATs.
                v3.11.5: always rendered; shows a placeholder chip when no LCATs
                are mapped to any Task Area yet (instead of hidden). */}
            {(() => {
              const hasCoverage = coverageMap && coverageMap.task_areas
                && coverageMap.task_areas.some(ta => !ta.is_unassigned && ta.lcats.count > 0);
              return (
                <div
                  className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px]"
                  data-testid="gap-tracker-task-area-coverage"
                >
                  <span className="text-slate-500 mr-1">LCAT coverage by Task Area:</span>
                  {hasCoverage ? (
                    coverageMap.task_areas.filter(ta => !ta.is_unassigned && ta.lcats.count > 0).map(ta => (
                      <button
                        key={ta.name}
                        type="button"
                        onClick={() => {
                          // Scroll up to Capability Matrix section
                          const el = document.querySelector('[data-testid="capability-matrix-section"]');
                          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-cyan-50 hover:bg-cyan-100 text-cyan-700 border border-cyan-200 transition-colors"
                        title={`${ta.lcats.count} LCAT${ta.lcats.count === 1 ? '' : 's'} mapped to ${ta.name} (${ta.lcats.gap} gap${ta.lcats.gap === 1 ? '' : 's'}, ${ta.lcats.partial} partial, ${ta.lcats.met} met)`}
                      >
                        <span className="font-medium">{ta.name}</span>
                        <span className="text-cyan-500">→</span>
                        <span>
                          {ta.lcats.count} LCAT{ta.lcats.count === 1 ? '' : 's'}
                        </span>
                        {ta.lcats.high_severity > 0 && (
                          <span className="ml-0.5 px-1 py-0 text-[9px] font-semibold rounded bg-red-100 text-red-700 border border-red-200">
                            {ta.lcats.high_severity} HIGH
                          </span>
                        )}
                      </button>
                    ))
                  ) : (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-slate-50 text-slate-500 border border-dashed border-slate-300 italic"
                      data-testid="gap-tracker-task-area-coverage-empty"
                      title="Populate Task Areas on LCATs in the Capability Matrix to see Task-Area → LCAT coverage here."
                    >
                      No Task Areas mapped yet
                    </span>
                  )}
                </div>
              );
            })()}
          </CardHeader>
          <CardContent className="p-0">
            {/* v3.10.11: column headers are now always visible — even
                when there are no requirements yet — so users see the
                structure of the data they're about to enter. The
                empty-state CTA renders inside the table body. */}
            <div className="overflow-x-auto">
              <Table data-testid="gap-tracker-table">
                <TableHeader className="bg-slate-50">
                  <TableRow>
                    <TableHead className="w-[36px]"></TableHead>
                    <TableHead>Requirement</TableHead>
                    <TableHead className="w-[110px]">Severity</TableHead>
                    <TableHead className="w-[140px]">Solution</TableHead>
                    <TableHead className="w-[220px]">Past Performance</TableHead>
                    <TableHead className="w-[120px]">Status</TableHead>
                    <TableHead className="w-[110px] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {gaps.length === 0 ? (
                    <TableRow data-testid="gap-tracker-empty-row">
                      <TableCell colSpan={7} className="py-12 text-center">
                        <UsersRound className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500 mb-4 text-sm">No requirements added yet</p>
                        <Button onClick={() => openModal()} data-testid="add-first-requirement-btn">
                          <Plus className="w-4 h-4 mr-2" />
                          Add First Requirement
                        </Button>
                      </TableCell>
                    </TableRow>
                  ) : (
                    gaps.map((gap) => (
                      <TableRow key={gap.id} data-testid={`gap-row-${gap.id}`}>
                        <TableCell className="align-top pt-4">
                          {getStatusIcon(gap.status)}
                        </TableCell>
                        <TableCell className="align-top">
                          <p className="font-medium text-slate-900">{gap.requirement_name}</p>
                          {gap.requirement_description && (
                            <p className="text-sm text-slate-500 mt-0.5">{gap.requirement_description}</p>
                          )}
                          {gap.requirement_source && (
                            <p className="text-xs text-slate-400 mt-1">Source: {gap.requirement_source}</p>
                          )}
                          {/* Federal-template narrative badges (v3.11.2) */}
                          {(gap.capability_statement || gap.bidder_response) && (
                            <div className="flex flex-wrap items-center gap-1 mt-1.5">
                              {gap.capability_statement && (
                                <button
                                  type="button"
                                  data-testid={`gap-${gap.id}-cap-stmt-badge`}
                                  onClick={() => openModal(gap)}
                                  className="text-[10px] px-1.5 py-0.5 rounded border bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 transition-colors"
                                  title={gap.capability_statement}
                                >
                                  ✓ Capability ({gap.capability_statement.length} chars)
                                </button>
                              )}
                              {gap.bidder_response && (
                                <button
                                  type="button"
                                  data-testid={`gap-${gap.id}-bidder-resp-badge`}
                                  onClick={() => openModal(gap)}
                                  className="text-[10px] px-1.5 py-0.5 rounded border bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100 transition-colors"
                                  title={gap.bidder_response}
                                >
                                  ✎ Response ({gap.bidder_response.length} chars)
                                </button>
                              )}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="align-top pt-4">
                          {getSeverityBadge(gap.severity)}
                        </TableCell>
                        <TableCell className="align-top pt-4">
                          {gap.solution_type ? (
                            <Badge variant="outline" className="text-xs">{gap.solution_type}</Badge>
                          ) : (
                            <span className="text-xs text-slate-400">—</span>
                          )}
                        </TableCell>
                        <TableCell className="align-top pt-3">
                          <div className="flex items-center gap-2">
                            <Link2 className="w-3 h-3 text-slate-400 shrink-0" />
                            <Select
                              value={gap.mapped_past_performance_id || 'none'}
                              onValueChange={(v) => handleMappingChange(gap.id, v === 'none' ? null : v)}
                            >
                              <SelectTrigger className="h-8 text-xs">
                                <SelectValue placeholder="Map to PP" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">Not Mapped</SelectItem>
                                {pastPerformances.map(pp => (
                                  <SelectItem key={pp.id} value={pp.id}>
                                    {pp.contract_title?.substring(0, 40)}{(pp.contract_title?.length || 0) > 40 ? '…' : ''}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </TableCell>
                        <TableCell className="align-top pt-3">
                          <Select
                            value={gap.status}
                            onValueChange={(v) => handleStatusChange(gap.id, v)}
                          >
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="GAP">Gap</SelectItem>
                              <SelectItem value="PARTIAL">Partial</SelectItem>
                              <SelectItem value="MET">Met</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="align-top pt-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openModal(gap)}
                              data-testid={`gap-edit-${gap.id}`}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(gap.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`gap-delete-${gap.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-6 p-4 bg-white rounded-lg border border-slate-200">
          <Button 
            variant="outline"
            onClick={() => navigate(`/${workspaceId}/pursuit/${pursuitId}/gonogo`)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Go/No-Go
          </Button>
          <Button
            className="bg-blue-600 hover:bg-blue-700 text-white"
            onClick={() => navigate(`/${workspaceId}/pursuit/${pursuitId}/strategy`)}
          >
            Strategy
            <ChevronRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="gap-modal">
          <DialogHeader>
            <DialogTitle>
              {editingGap ? 'Edit Requirement' : 'Add Requirement Gap'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            <div className="space-y-2">
              <Label>Requirement Name *</Label>
              <Input
                value={form.requirement_name}
                onChange={(e) => setForm({...form, requirement_name: e.target.value})}
                placeholder="e.g., ISO 27001 Certification"
                data-testid="gap-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={form.requirement_description}
                onChange={(e) => setForm({...form, requirement_description: e.target.value})}
                placeholder="Detailed description..."
                rows={2}
              />
            </div>

            {/* v3.12.2 — Task Area input. Drives Cross-Tool Coverage Map
                roll-up. We surface the existing Task Areas from this
                pursuit's Capability Matrix as datalist suggestions so
                reviewers stay consistent across tools. */}
            <div className="space-y-2">
              <Label>
                Task Area
                <span className="text-xs text-slate-500 ml-2">
                  (groups this gap on the Coverage Map)
                </span>
              </Label>
              <Input
                list="gap-task-area-suggestions"
                value={form.task_area}
                onChange={(e) => setForm({ ...form, task_area: e.target.value })}
                placeholder="e.g., Cloud Engineering"
                data-testid="gap-task-area-input"
              />
              <datalist id="gap-task-area-suggestions">
                {coverageMap?.task_areas
                  ?.filter((ta) => !ta.is_unassigned)
                  ?.map((ta) => (
                    <option key={ta.name} value={ta.name} />
                  ))}
              </datalist>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Source</Label>
                <Input
                  value={form.requirement_source}
                  onChange={(e) => setForm({...form, requirement_source: e.target.value})}
                  placeholder="e.g., RFP Section 4.1"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GAP">Gap</SelectItem>
                    <SelectItem value="PARTIAL">Partial</SelectItem>
                    <SelectItem value="MET">Met</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Severity</Label>
                <Select value={form.severity} onValueChange={(v) => setForm({...form, severity: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="HIGH">High</SelectItem>
                    <SelectItem value="MEDIUM">Medium</SelectItem>
                    <SelectItem value="LOW">Low</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Solution Type</Label>
                <Select value={form.solution_type || 'none'} onValueChange={(v) => setForm({...form, solution_type: v === 'none' ? '' : v})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="TEAMING">Teaming</SelectItem>
                    <SelectItem value="HIRE">Hire</SelectItem>
                    <SelectItem value="EXISTING">Existing Capability</SelectItem>
                    <SelectItem value="TRAINING">Training</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Map to Past Performance</Label>
              <Select 
                value={form.mapped_past_performance_id || 'none'} 
                onValueChange={(v) => setForm({...form, mapped_past_performance_id: v === 'none' ? '' : v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select past performance..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not Mapped</SelectItem>
                  {pastPerformances.map(pp => (
                    <SelectItem key={pp.id} value={pp.id}>
                      {pp.contract_title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Solution Notes</Label>
              <Textarea
                value={form.solution_notes}
                onChange={(e) => setForm({...form, solution_notes: e.target.value})}
                placeholder="Notes on how to address this gap..."
                rows={2}
              />
            </div>

            {/* v3.11.2 — Federal-template parity (Excel "Capabilities" sheet) */}
            <div className="space-y-2 pt-2 border-t border-slate-200">
              <Label className="flex items-center gap-2">
                <span>Capability Statement</span>
                <span className="text-[10px] text-slate-400 font-normal">(positive narrative for proposal)</span>
              </Label>
              <Textarea
                data-testid="gap-capability-statement"
                value={form.capability_statement}
                onChange={(e) => setForm({...form, capability_statement: e.target.value})}
                placeholder="Our team has implemented... We are positioned to deliver..."
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <span>Bidder Response / Notes</span>
                <span className="text-[10px] text-slate-400 font-normal">(internal narrative or response approach)</span>
              </Label>
              <Textarea
                data-testid="gap-bidder-response"
                value={form.bidder_response}
                onChange={(e) => setForm({...form, bidder_response: e.target.value})}
                placeholder="Approach: leverage existing… Add subcontractor for…"
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={closeModal}>Cancel</Button>
            <Button 
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="bg-teal-700 hover:bg-teal-800 text-white"
              data-testid="gap-submit-btn"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              {editingGap ? 'Update' : 'Add'} Requirement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
